import { ref } from 'vue';
import { type AxiosError, type AxiosResponse, isAxiosError } from 'axios';
import { useI18n } from '@/app/modules/i18n';

export enum RequestErrorType {
  ServerError = 'ServerError',
  BadRequest = 'BadRequest',
}

export interface RequestError {
  message: string;
  type: RequestErrorType;
  originalResponse?: AxiosResponse<any>;
}

export function useRequestError() {
  const { t } = useI18n();

  function getTransportErrorMessage(error: AxiosError<any>) {
    const message = error.response?.data?.errors?.[0] as string;

    if (message) {
      return message;
    }

    if (error.response?.status === 404) {
      return t('common.errors.notFound');
    }

    return t('common.errors.badRequest');
  }

  const requestError = ref<RequestError | null>();

  const parseError = (error: unknown): RequestError => {
    const result: RequestError = {
      message: t('common.errors.loadDataFailed'),
      type: RequestErrorType.ServerError,
    };

    if (isAxiosError(error)) {
      result.originalResponse = error.response;

      if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
        result.message = getTransportErrorMessage(error);
        result.type = RequestErrorType.BadRequest;
      }
    }

    requestError.value = result;

    return requestError.value;
  };

  const clearError = () => {
    requestError.value = null;
  };

  return { requestError, parseError, clearError };
}
